import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import CalendarSetup from "./CalendarSetup";
import LeftSidebar from "../components/sidebar";
import Header from "../components/Header";
import ProfileMenu from "../components/ProfileMenu";
import { useOutletContext } from "react-router-dom";


const SetupCalendarPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { routeLoading } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [googleConnected, setGoogleConnected] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [appleConnected, setAppleConnected] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showCalendarSetup, setShowCalendarSetup] = useState(false);
  const [manualSelected, setManualSelected] = useState(false);
  const [selectedCalendarName, setSelectedCalendarName] = useState("");
  const [selectedProviderType, setSelectedProviderType] = useState("");
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [pendingDisconnectProvider, setPendingDisconnectProvider] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [connRes, setupRes] = await Promise.all([
          api.get("/calendar/setup-calendar"),
          api.get("/user/calendar-setup"),
        ]);
        const connData = connRes.data || {};
        setGoogleConnected(!!connData.googleConnected);
        setMicrosoftConnected(!!connData.microsoftConnected);
        setAppleConnected(!!connData.appleConnected);

        const setupData = setupRes.data || {};
        const selected = setupData.selected || {};
        const providerType = selected.provider_type || "";
        const calendars = setupData.options?.calendars || [];
        const selectedCalendarUid = selected.calendar_uid || "";
        const matchedCalendar = calendars.find((c) => c.uid === selectedCalendarUid);
        const calendarName =
          matchedCalendar?.name ||
          selected.calendar_name ||
          setupData.calendar_name ||
          "";

        setSelectedCalendarName(calendarName);
        setSelectedProviderType(providerType || matchedCalendar?.provider_type || "");
        setManualSelected((providerType || matchedCalendar?.provider_type || "") === "manual");
      } catch (e) {
        console.error("Failed to load setup-calendar state", e);
        setError("Failed to load calendar connection state.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [location.key]);

  const handleDisconnect = (provider) => {
    setPendingDisconnectProvider(provider);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    const provider = pendingDisconnectProvider;
    if (!provider) {
      setShowDisconnectModal(false);
      return;
    }

    try {
      setDisconnecting(true);
      if (provider === "google") {
        await api.post("/calendar/disconnect/google");
        setGoogleConnected(false);
      } else if (provider === "microsoft") {
        await api.post("/calendar/disconnect/microsoft");
        setMicrosoftConnected(false);
      } else if (provider === "apple") {
        await api.post("/calendar/disconnect/apple");
        setAppleConnected(false);
      } else if (provider === "manual") {
        await api.post("/calendar/setup/reset");
        setManualSelected(false);
      } else {
        await api.post("/calendar/disconnect/all");
        setGoogleConnected(false);
        setMicrosoftConnected(false);
        setAppleConnected(false);
        setManualSelected(false);
      }
      setSelectedCalendarName("");
      setSelectedProviderType("");
      // After any full disconnect, allow inline setup again
      if (provider === "all" || provider === "manual") {
        setShowCalendarSetup(false);
      }
    } catch (e) {
      console.error("Failed to disconnect calendar", e);
      alert("Failed to disconnect calendar.");
    } finally {
      setDisconnecting(false);
      setShowDisconnectModal(false);
      setPendingDisconnectProvider("");
    }
  };

  const handleOpenCalendarSetup = () => {
  sessionStorage.setItem("from_disconnect_page", "true");
  navigate("/calendar-setup");
};




  if (routeLoading || loading) {
  return null; // AppLayout loader will show
}

  const nothingConnected =
    !googleConnected && !microsoftConnected && !appleConnected && !manualSelected && !selectedCalendarName;

  return (
  <main className="dashboard-screen">
    <div className="container-fluid">
      <div className="row gx-5">
        <div className="col-lg-3 left-sidebar-main">
          <LeftSidebar />
        </div>
        <div className="col-lg-9">
          <Header />
          <div className="row contact-section" >
            <div className="col-lg-4 profile-sidebar-menu">
              <div className="sec1">
                <ProfileMenu />
              </div>
            </div>
            <div className="col-lg-8">
              <div className="sec2">
                <div className="form-section">
                  <div className="row mb-3">
                    <div className="col-sm-12">
                    <h3>Calendar Integration</h3>
                    <p>Change calendar source</p>
                    </div>
                  </div>
                    {error && <div className="alert alert-danger">{error}</div>}

                    {selectedCalendarName && !googleConnected && !microsoftConnected && !appleConnected && (
                      <div className="dash-section2">
                        <div className="setup_cal">
                          <p className="mb-0">
                            <div className="status"><span className="green-dot"></span>Connected</div>
                          </p>
                          <h5 className="status-msg mb-2">{selectedCalendarName}</h5>
                          <div className="sync">Selected calendar</div>
                          <button
                            className="btn5 mt-3"
                            onClick={() => handleDisconnect("manual")}
                            disabled={disconnecting}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}

                    {googleConnected && selectedCalendarName && (
                      <div className="dash-section2">
                        <div className="setup_cal">
                          <p className="mb-0">
                            <div className="status"><span className="green-dot"></span>Connected</div>
                          </p>
                          <h5 className="status-msg mb-2">{selectedCalendarName}</h5>
                          <div className="sync">Selected calendar</div>
                          <button
                            className="btn5 mt-3"
                            onClick={() => handleDisconnect("google")}
                            disabled={disconnecting}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}

                    {microsoftConnected && (
                      <div className="dash-section2">
                        <div className="setup_cal">
                          <p className="mb-0">
                            <div className="status"><span className="green-dot"></span>Connected</div>
                          </p>
                          <h5 className="status-msg mb-2">Microsoft Calendar</h5>
                          <div className="sync">Selected calendar</div>
                          <button
                            className="btn5 mt-3"
                            onClick={() => handleDisconnect("microsoft")}
                            disabled={disconnecting}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}

                    {appleConnected && selectedCalendarName && (
                      <div className="dash-section2">
                        <div className="setup_cal">
                          <p className="mb-0">
                            <div className="status"><span className="green-dot"></span>Connected</div>
                          </p>
                          <h5 className="status-msg mb-2">{selectedCalendarName}</h5>
                          <div className="sync">Selected calendar</div>
                          <button
                            className="btn5 mt-3"
                            onClick={() => handleDisconnect("apple")}
                            disabled={disconnecting}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}

                    {nothingConnected && (
                      <>
                        <div className="mb-3">
                          <button
  className="btn5"
  onClick={handleOpenCalendarSetup}
>
  <i className="bi bi-calendar-event"></i> Calendar Setup
</button>

                        </div>
                      </>
                    )}

                   {showDisconnectModal && (
  <div
    className="modal popup_dark fade show d-block"
    tabIndex="-1"
    role="dialog"
    aria-modal="true"
    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              setShowDisconnectModal(false);
              setPendingDisconnectProvider("");
            }}
            disabled={disconnecting}
          />
        </div>

        <div className="modal-body text-center">
          <h5>
            Are you sure you want to disconnect your{" "}
            {pendingDisconnectProvider === "google"
              ? "Google Calendar"
              : pendingDisconnectProvider === "microsoft"
              ? "Microsoft Calendar"
              : pendingDisconnectProvider === "apple"
              ? "Apple Calendar"
              : "Manual File Upload"}
            ?
          </h5>

          <p>Your duties, schedules, and alerts will stop syncing.</p>

          <div className="call_action">
            <button
              className="btn6"
              onClick={confirmDisconnect}
              disabled={disconnecting}
            >
              Yes
            </button>

            <button
              className="btn7"
              onClick={() => {
                setShowDisconnectModal(false);
                setPendingDisconnectProvider("");
              }}
              disabled={disconnecting}
            >
              No
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}


                    {showCalendarSetup && (
                      <div className="card mt-3 shadow-sm">
                        <div className="card-body">
                          <CalendarSetup />
                        </div>
                      </div>
                    )}

                
                 
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
  );
};

export default SetupCalendarPage;
